import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import type { MediaType, Profile } from '@teaplus/shared';
import { api } from './api';
import { useDiscordStatus } from './discord';
import { nowPlaying } from './now-playing';
import { useLinks } from './party';
import { lookingAt, placeText } from './presence';
import { useActiveProfile, useSession } from './queries';

/** What the Windows app shows on the person's Discord profile (Discord's Rich Presence). */
export interface DiscordActivity {
  /** 0 Playing ("Playing Tea+") while browsing, 3 Watching while a film or episode plays. */
  type: 0 | 3;
  details: string;
  state?: string;
  timestamps?: { start: number; end?: number };
  assets?: {
    large_image?: string;
    large_text?: string;
    /** Where clicking the picture goes. */
    large_url?: string;
    small_image?: string;
    small_text?: string;
    small_url?: string;
  };
  buttons?: { label: string; url: string }[];
}

export interface DiscordRpcState {
  /** Whether Discord is open on this PC. */
  connected: boolean;
  /** Who is signed in to the Discord app on this PC (missing in apps before 0.6.2). */
  user?: { id: string; username: string } | null;
  /** Whether that's the profile's own Discord, so the status shows. */
  matches?: boolean;
}

/** In the Windows app (preload.cjs): talks to the Discord app running on the same PC. */
export interface DiscordBridge {
  /** expectUser: the profile's own Discord user id. Nothing shows on anyone else's Discord. */
  setActivity: (
    clientId: string,
    activity: DiscordActivity | null,
    expectUser?: string,
  ) => Promise<DiscordRpcState>;
  getState: () => Promise<DiscordRpcState>;
}

export const discordBridge = (): DiscordBridge | null => window.teaplus?.discord ?? null;

export const TEAPLUS_ICON_URL = 'https://teaplus.tv/images/icon.png';
/** Where the pictures and the Get Tea+ button lead: the website, to see and download Tea+. */
export const TEAPLUS_SITE_URL = 'https://www.teaplus.tv';
export const TEAPLUS_TAGLINE = 'Tea+ - Your Right To Stream';
const GET_TEAPLUS = { label: 'Get Tea+', url: TEAPLUS_SITE_URL };
/** When the app opened: browsing shows the time since then, not since the last page change. */
const OPENED_AT = Date.now();

/** Discord fetches the pictures itself, so only addresses it can reach are any use. */
const publicImage = (url: string | null | undefined) =>
  url && /^https:\/\//i.test(url) && !/^https:\/\/(localhost|127\.|\[::1\])/i.test(url)
    ? url
    : undefined;

const clip = (s: string, max = 128) => (s.length > max ? `${s.slice(0, max - 1)}…` : s);
// Discord needs at least two characters in details and state.
const text = (s: string) => clip(s.trim().padEnd(2, ' '));

export interface PresenceInput {
  now: number;
  /** When this Tea+ session started (the browsing clock). */
  openedAt?: number;
  profile: { name: string; avatarUrl: string | null };
  /** "Browsing Movies", "Looking at Interstellar". */
  browsing: string;
  linkUrl: string;
  playing: {
    type: MediaType;
    id: string;
    title: string;
    posterUrl: string | null;
    year: number | null;
    season: number | null;
    episode: number | null;
    episodeTitle: string | null;
    position: number;
    duration: number;
    paused: boolean;
    inParty: boolean;
    /** Watched privately: Discord never sees which title it is. */
    private: boolean;
  } | null;
}

export function buildActivity(p: PresenceInput): DiscordActivity {
  const avatar = publicImage(p.profile.avatarUrl);
  const w = p.playing;
  const brand = {
    small_image: TEAPLUS_ICON_URL,
    small_text: TEAPLUS_TAGLINE,
    small_url: TEAPLUS_SITE_URL,
  };
  if (!w || w.private) {
    return {
      type: w ? 3 : 0,
      details: text(w ? 'Watching something' : TEAPLUS_TAGLINE),
      state: text(w ? TEAPLUS_TAGLINE : p.browsing),
      ...(!w && p.openedAt ? { timestamps: { start: p.openedAt } } : {}),
      assets: {
        large_image: avatar ?? TEAPLUS_ICON_URL,
        large_text: clip(avatar ? p.profile.name : TEAPLUS_TAGLINE),
        large_url: TEAPLUS_SITE_URL,
        ...(avatar ? brand : {}),
      },
      buttons: [GET_TEAPLUS],
    };
  }
  const what =
    w.type === 'series' && w.season !== null && w.episode !== null
      ? `S${w.season} · E${w.episode}${w.episodeTitle ? ` · ${w.episodeTitle}` : ''}`
      : w.year
        ? `Film · ${w.year}`
        : 'Film';
  const state = [w.paused ? 'Paused' : null, what, w.inParty ? 'Watch Together' : null]
    .filter(Boolean)
    .join(' · ');
  const start = Math.round(p.now - w.position * 1000);
  const link = /^https:\/\//i.test(p.linkUrl) ? `${p.linkUrl}/${w.type}/${w.id}` : null;
  return {
    type: 3,
    details: text(`Watching ${w.title}`),
    state: text(state),
    ...(!w.paused && w.duration > 0
      ? { timestamps: { start, end: Math.round(start + w.duration * 1000) } }
      : {}),
    assets: {
      large_image: publicImage(w.posterUrl) ?? avatar ?? TEAPLUS_ICON_URL,
      large_text: clip(w.year ? `${w.title} (${w.year})` : w.title),
      large_url: TEAPLUS_SITE_URL,
      ...brand,
    },
    buttons: [
      ...(link
        ? [{ label: w.type === 'series' ? 'View the show' : 'View the film', url: link }]
        : []),
      GET_TEAPLUS,
    ],
  };
}

/** Same activity apart from the clock drifting a few seconds. */
const sameActivity = (a: DiscordActivity | null, b: DiscordActivity | null) => {
  const norm = (x: DiscordActivity | null) =>
    x && {
      ...x,
      timestamps: x.timestamps && {
        start: Math.round(x.timestamps.start / 5000),
        end: x.timestamps.end && Math.round(x.timestamps.end / 5000),
      },
    };
  return JSON.stringify(norm(a)) === JSON.stringify(norm(b));
};

const CHECK_EVERY_MS = 4000;

/**
 * Keeps Discord showing what this profile is doing, while Enable Discord Presence is on, Discord
 * is connected and this is the Windows app. avatarEl holds the profile's avatar as drawn, which
 * is uploaded once (and again when it changes) so Discord can show it.
 */
export function useDiscordPresence(avatarEl: React.RefObject<HTMLElement | null>) {
  const bridge = discordBridge();
  const session = useSession().data;
  const active = useActiveProfile().data;
  const status = useDiscordStatus();
  const { linkUrl } = useLinks();
  const { pathname } = useLocation();
  const profile = active?.profile;
  const prefs = active?.preferences;
  const clientId = status.data?.clientId ?? null;
  // The profile's own Discord: the status shows only there, never on another profile's.
  const own = status.data?.profile?.id ?? '';
  const on = !!bridge && !!session && !!profile && !!clientId && !!own && !!prefs?.discordPresence;
  const avatarUrl = usePresenceAvatar(on ? profile : undefined, avatarEl);
  // undefined: nothing sent yet, so the next check always sends (null clears the status).
  const sent = useRef<DiscordActivity | null | undefined>(undefined);
  const compute = useRef<() => DiscordActivity | null>(() => null);

  compute.current = () => {
    if (!on || !profile || !prefs) return null;
    const n = nowPlaying();
    const looking = lookingAt();
    const privateIds = new Set(prefs.privateTitles);
    const video = n ? document.querySelector('video') : null;
    return buildActivity({
      now: Date.now(),
      openedAt: OPENED_AT,
      profile: { name: profile.name, avatarUrl },
      browsing:
        looking && /^\/(movie|series)\//.test(pathname) && !privateIds.has(looking.mediaId)
          ? `Looking at ${looking.title}`
          : placeText(pathname),
      linkUrl,
      playing: n
        ? {
            type: n.type,
            id: n.id,
            title: n.title,
            posterUrl: n.posterUrl,
            year: n.year,
            season: n.season,
            episode: n.episode,
            episodeTitle: n.episodeTitle,
            position: n.position(),
            duration: video && Number.isFinite(video.duration) ? video.duration : n.duration(),
            paused: !n.playing(),
            inParty: n.inParty,
            private: privateIds.has(n.id),
          }
        : null,
    });
  };

  useEffect(() => {
    if (!bridge) return;
    const tick = () => {
      const next = compute.current();
      if (sent.current !== undefined && sameActivity(sent.current, next)) return;
      sent.current = next;
      void bridge.setActivity(clientId ?? '', next, own).catch(() => undefined);
    };
    // Another profile's Discord: send again so the app checks who is signed in to Discord.
    sent.current = undefined;
    tick();
    const t = window.setInterval(tick, CHECK_EVERY_MS);
    return () => window.clearInterval(t);
  }, [bridge, clientId, on, own, pathname, avatarUrl]);

  // Clears the status when this goes away (signed out, app closing).
  useEffect(() => {
    if (!bridge) return;
    return () => {
      sent.current = undefined;
      void bridge.setActivity('', null).catch(() => undefined);
    };
  }, [bridge]);
}

/** The address of the profile's avatar for Discord, uploading it when it has changed. */
function usePresenceAvatar(
  profile: Profile | undefined,
  avatarEl: React.RefObject<HTMLElement | null>,
) {
  const sig = profile ? `${profile.avatar}|${profile.avatarUrl ?? ''}` : '';
  const storeKey = profile ? `tp:presence-avatar:${profile.id}` : '';
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!storeKey) return setUrl(null);
    try {
      const saved = JSON.parse(localStorage.getItem(storeKey) ?? 'null') as {
        sig: string;
        url: string;
      } | null;
      if (saved?.sig === sig) return setUrl(saved.url);
    } catch {}
    let cancelled = false;
    // A moment for the hidden avatar (and a photo) to draw first.
    const t = window.setTimeout(async () => {
      const { avatarPng } = await import('@/components/profile/avatar-png');
      const avatar = await avatarPng(avatarEl.current);
      if (!avatar || cancelled) return;
      const r = await api<{ url: string }>('/api/presence/avatar', {
        method: 'PUT',
        body: { avatar },
      }).catch(() => null);
      if (!r || cancelled) return;
      try {
        localStorage.setItem(storeKey, JSON.stringify({ sig, url: r.url }));
      } catch {}
      setUrl(r.url);
    }, 800);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [storeKey, sig, avatarEl]);
  return url;
}

/** For Settings: whether Discord is open and showing the status right now. */
export function useDiscordRpcState(enabled: boolean) {
  const bridge = discordBridge();
  const [state, setState] = useState<DiscordRpcState | null>(null);
  useEffect(() => {
    if (!bridge || !enabled) return setState(null);
    const read = () => void bridge.getState().then(setState, () => undefined);
    read();
    const t = window.setInterval(read, 3000);
    return () => window.clearInterval(t);
  }, [bridge, enabled]);
  return state;
}
